import React, { useEffect, useRef, useState } from 'react';
import { __ } from '@wordpress/i18n';

// icons
import FilterIcon from '../../../../icons/FilterIcon';
import PlusIcon from '../../../../icons/PlusIcon';
import SearchIcon from '../../../../icons/SearchIcon';

// store
import { useCommCenter } from '../../context';

/**
 * Search / filter / new comment row above the communications feed.
 */
const CommentToolbar = () => {
    const {
        commentSearch,
        setCommentSearch,
        commentFilter,
        setCommentFilter,
        addComment,
        can,
        pending,
        composerSeed,
        clearComposerSeed,
        composerCueLabels,
    } = useCommCenter();

    const [filterOpen, setFilterOpen] = useState(false);
    const [composerOpen, setComposerOpen] = useState(false);
    const [draft, setDraft] = useState('');
    const filterRef = useRef(null);
    const composerRef = useRef(null);

    useEffect(() => {
        if (!filterOpen) return undefined;

        const onDocClick = (e) => {
            if (!filterRef.current?.contains(e.target)) setFilterOpen(false);
        };

        document.addEventListener('mousedown', onDocClick);

        return () => document.removeEventListener('mousedown', onDocClick);
    }, [filterOpen]);

    useEffect(() => {
        if (composerOpen) composerRef.current?.focus();
    }, [composerOpen]);

    // "Commenta" on an activity hands the composer a pre-filled mention + cue.
    useEffect(() => {
        if (!composerSeed) return;

        setDraft(composerSeed);
        setComposerOpen(true);
        clearComposerSeed();
    }, [composerSeed, clearComposerSeed]);

    const filters = [
        { id: 'all', label: __('Tutti i commenti', 'soundbetween') },
        { id: 'unresolved', label: __('Da risolvere', 'soundbetween') },
        { id: 'resolved', label: __('Risolti', 'soundbetween') },
        { id: 'external', label: __('Da fonti esterne', 'soundbetween') },
    ];

    const submit = async () => {
        if (!draft.trim()) return;

        await addComment(draft);
        setDraft('');
        setComposerOpen(false);
    }

    return (
        <div className="commToolbar">
            <div className="commToolbar__row">
                <label className="commSearch">
                    <span className="commSearch__icon"><SearchIcon/></span>
                    <input
                        type="search"
                        value={commentSearch}
                        onChange={(e) => setCommentSearch(e.target.value)}
                        placeholder={__('Cerca nei commenti', 'soundbetween')}/>
                </label>

                <div className="commToolbar__filter" ref={filterRef}>
                    <button
                        type="button"
                        className={`commIconButton ${commentFilter !== 'all' ? 'is-active' : ''}`}
                        onClick={() => setFilterOpen(!filterOpen)}
                        title={__('Filtra i commenti', 'soundbetween')}>
                        <FilterIcon/>
                    </button>
                    {filterOpen && (
                        <ul className="commToolbar__filterMenu">
                            {filters.map(filter => (
                                <li key={filter.id}>
                                    <button
                                        type="button"
                                        className={commentFilter === filter.id ? 'is-active' : ''}
                                        onClick={() => {
                                            setCommentFilter(filter.id);
                                            setFilterOpen(false);
                                        }}>
                                        {filter.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {can.comment && (
                    <button
                        type="button"
                        className="commIconButton commIconButton--accent"
                        onClick={() => setComposerOpen(!composerOpen)}
                        title={__('Aggiungi un commento', 'soundbetween')}>
                        <PlusIcon/>
                    </button>
                )}
            </div>

            {composerOpen && (
                <div className="commComposer">
                    <textarea
                        ref={composerRef}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder={__('Scrivi un commento…', 'soundbetween')}/>
                    {composerCueLabels?.length > 0 && (
                        // The selected cues are linked to the comment: say so
                        // before it is published, not after.
                        <p className="commComposer__link">
                            {__('Collegato a:', 'soundbetween')} <strong>{composerCueLabels.join(', ')}</strong>
                        </p>
                    )}
                    <div className="commComposer__actions">
                        <button
                            type="button"
                            className="commButton commButton--ghost"
                            onClick={() => { setComposerOpen(false); setDraft(''); }}>
                            {__('Annulla', 'soundbetween')}
                        </button>
                        <button
                            type="button"
                            className="commButton commButton--accent"
                            disabled={!draft.trim() || pending?.comment}
                            onClick={submit}>
                            {pending?.comment
                                ? __('Pubblico…', 'soundbetween')
                                : __('Pubblica', 'soundbetween')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default CommentToolbar;
